// Phase HUB-DRILL-1 — Micro Hub full-page route + drawer-like rendering.
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, Building2, ChevronDown, ChevronRight, Mail, Phone, MapPin, Users,
} from "lucide-react";
import { useState } from "react";
import RoleGate from "../../components/RoleGate";
import useTimedFetch from "../../hooks/useTimedFetch";
import StatusBadge from "../../components/hub/StatusBadge";
import { useOrderDrawer } from "../../components/hub/OrderDrawerContext";
import { HubDetailError, HubDetailLoading } from "../../components/founder/MainHubDetailView";

const ORANGE = "#FF6B00";

function StatusPill({ s }) {
  if (!s) return null;
  const map = {
    active:           "bg-emerald-100 text-emerald-700 border-emerald-200",
    inactive:         "bg-slate-100 text-slate-600 border-slate-200",
    pending_approval: "bg-amber-100 text-amber-700 border-amber-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${map[s] || map.inactive}`}>
      {s.replace(/_/g, " ")}
    </span>
  );
}

function Section({ title, count, children, defaultOpen = true, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={testid}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center px-4 py-3 hover:bg-slate-50">
        {open ? <ChevronDown className="h-4 w-4 mr-2 text-slate-500"/> : <ChevronRight className="h-4 w-4 mr-2 text-slate-500"/>}
        <span className="text-sm font-semibold">{title}</span>
        {typeof count === "number" && (
          <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-[#C2410C] font-semibold">{count}</span>
        )}
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </section>
  );
}

function PeopleList({ rows, testid }) {
  if (!rows || rows.length === 0) {
    return <div className="text-xs text-slate-400 italic">None.</div>;
  }
  return (
    <ul className="divide-y divide-slate-100" data-testid={testid}>
      {rows.map((r) => (
        <li key={r.user_id} className="py-2 flex items-start gap-3 text-xs">
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-800">{r.name || r.email}</div>
            <div className="text-slate-500 text-[11px] inline-flex items-center gap-2">
              {r.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3"/>{r.email}</span>}
              {r.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3"/>{r.phone}</span>}
            </div>
            {(r.capabilities || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {r.capabilities.map((c) => (
                  <span key={c} className="text-[9px] px-1 py-0.5 rounded bg-orange-50 text-[#C2410C] border border-orange-100 uppercase tracking-wider">{c}</span>
                ))}
              </div>
            )}
          </div>
          <StatusPill s={r.status}/>
        </li>
      ))}
    </ul>
  );
}

function TidLink({ tid }) {
  const { open } = useOrderDrawer();
  if (!tid) return <span className="text-slate-400">—</span>;
  return (
    <button onClick={() => open(tid)}
            data-testid={`tid-link-${tid}`}
            className="font-mono text-xs text-[#C2410C] hover:underline">
      {tid}
    </button>
  );
}

function Body() {
  const { hubId } = useParams();
  const { data, loading, error, reload } = useTimedFetch(`/founder/micro-hubs/${hubId}`);
  const h = data?.hub || {};
  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-3" data-testid="founder-micro-hub-detail-page">
      <div className="flex items-center gap-2">
        <Link to="/dashboard/founder/micro-hubs" className="text-[#FF6B00] hover:opacity-80"
              data-testid="micro-hub-detail-back">
          <ArrowLeft className="h-5 w-5"/>
        </Link>
        <div className="text-xs text-slate-500">Micro Hub</div>
        <span className="font-mono text-sm text-slate-700">{hubId}</span>
      </div>

      {loading && !data && <HubDetailLoading/>}
      {error && <HubDetailError {...error} onRetry={reload}/>}
      {data && (
        <>
          <section className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="micro-hub-header">
            <div className="flex items-center gap-2 flex-wrap">
              <Building2 className="h-5 w-5 text-[#FF6B00]"/>
              <h2 className="font-['Outfit'] text-xl font-bold">{h.name || "Micro hub"}</h2>
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">{h.hub_id}</span>
              <StatusPill s={h.status}/>
            </div>
            <div className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1.5">
              <MapPin className="h-3 w-3"/> {h.city || "—"}{h.pincode ? ` · ${h.pincode}` : ""}
            </div>
            {h.address && <div className="text-xs text-slate-600 mt-1">{h.address}</div>}
            {data?.parent_main_hub && (
              <div className="mt-2 text-xs">
                Parent main hub: <Link to={`/dashboard/founder/main-hubs/${data.parent_main_hub.hub_id}`}
                                          className="text-[#C2410C] hover:underline font-medium">
                  {data.parent_main_hub.name}
                </Link>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
              <div><div className="text-[10px] uppercase text-slate-500">Orders today</div><div className="text-base font-bold text-[#FF6B00]">{data?.metrics?.orders_today ?? 0}</div></div>
              <div><div className="text-[10px] uppercase text-slate-500">Pending</div><div className="text-base font-bold">{data?.metrics?.pending_orders_count ?? 0}</div></div>
              <div><div className="text-[10px] uppercase text-slate-500">Staff</div><div className="text-base font-bold inline-flex items-center gap-1"><Users className="h-3 w-3"/>{data?.metrics?.staff_count ?? 0}</div></div>
            </div>
          </section>

          <Section title="Lead" testid="micro-hub-lead-section">
            {data?.lead?.name
              ? <div className="text-xs">
                  <div className="font-semibold">{data.lead.name}</div>
                  <div className="text-slate-500 inline-flex items-center gap-2">
                    {data.lead.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3"/>{data.lead.email}</span>}
                    {data.lead.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3"/>{data.lead.phone}</span>}
                  </div>
                  <StatusPill s={data.lead.status}/>
                </div>
              : <div className="text-xs text-slate-400 italic">Unassigned</div>}
          </Section>

          <Section title="Packers" count={data?.packers?.length || 0} testid="micro-hub-packers-section">
            <PeopleList rows={data?.packers} testid="micro-hub-packers-list"/>
          </Section>

          <Section title="Pickup agents" count={data?.pickup_agents?.length || 0} testid="micro-hub-pickup-agents-section">
            <PeopleList rows={data?.pickup_agents} testid="micro-hub-pickup-agents-list"/>
          </Section>

          <Section title="Delivery agents" count={data?.delivery_agents?.length || 0} testid="micro-hub-delivery-agents-section">
            <PeopleList rows={data?.delivery_agents} testid="micro-hub-delivery-agents-list"/>
          </Section>

          <Section title="Recent orders" count={data?.recent_orders?.length || 0} testid="micro-hub-recent-orders-section">
            {(!data.recent_orders || data.recent_orders.length === 0)
              ? <div className="text-xs text-slate-400 italic">No orders yet.</div>
              : (
                <div className="overflow-x-auto -mx-4">
                  <table className="w-full text-xs min-w-[700px]">
                    <thead className="bg-slate-50/70">
                      <tr className="text-left text-slate-500">
                        <th className="px-3 py-2 text-[10px] uppercase tracking-wider">Tracking</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-wider">Status</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-wider">Customer</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-wider">Pickup</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-wider">Packer</th>
                        <th className="px-3 py-2 text-[10px] uppercase tracking-wider">Delivery</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {data.recent_orders.map((o) => (
                        <tr key={o.tracking_id}>
                          <td className="px-3 py-2"><TidLink tid={o.tracking_id}/></td>
                          <td className="px-3 py-2"><StatusBadge status={o.fulfillment_status} size="sm"/></td>
                          <td className="px-3 py-2">{o.customer_name || "—"}</td>
                          <td className="px-3 py-2">{o.pickup_agent_name || <span className="text-slate-400">—</span>}</td>
                          <td className="px-3 py-2">{o.packer_name || <span className="text-slate-400">—</span>}</td>
                          <td className="px-3 py-2">{o.delivery_agent_name || <span className="text-slate-400">—</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </Section>
        </>
      )}
    </div>
  );
}

export default function MicroHubDetailPage() {
  return <RoleGate allow={["founder", "admin", "main_hub_manager", "ceo"]}><Body/></RoleGate>;
}
